/**
 * Geteilte Shop-Header-Komponenten für Landing + PDP + Checkout.
 *
 * - DeliveryAnnouncementBar: dünne Top-Bar mit Liefer-Versprechen
 * - ShopHeader: sticky Hauptheader (Logo links, Nav rechts: Search/Cart/etc.)
 *
 * Ziel: Identisches Header-Verhalten zwischen allen Subdomain-Seiten,
 * sodass sich das Ganze wie ein zusammenhängender Shop anfühlt — nicht
 * wie eine persönliche Landing-Page.
 */
import { ArrowLeft, Headphones, PackageCheck, Truck } from "lucide-react";
import { type PublicPage, absoluteUploadUrl } from "../lib/page";
import { getThemeColors } from "../lib/theme_presets";
import { MiniCartButton } from "./MiniCart";
import { WishlistButton } from "./Wishlist";
import { CategoryMenu } from "./CategoryMenu";
import { CustomerProfileButton } from "./CustomerProfileButton";
import { navigate } from "../lib/router";

type ThemeT = ReturnType<typeof getThemeColors>;

// ============================================================
// Delivery-Promise-Bar — dünne Top-Bar oben auf jeder Subdomain-Seite
// ============================================================

export function DeliveryAnnouncementBar({
  isDark,
  hasWhatsApp = false,
}: {
  isDark: boolean;
  hasWhatsApp?: boolean;
}) {
  return (
    <div
      className="w-full text-[11px] md:text-xs"
      style={{
        background: isDark ? "rgba(255,255,255,.04)" : "#f5f0e6",
        color: isDark ? "rgba(255,255,255,.7)" : "#6b6b6b",
        borderBottom: isDark
          ? "1px solid rgba(255,255,255,.06)"
          : "1px solid rgba(0,0,0,.04)",
      }}
    >
      <div className="mx-auto max-w-6xl px-4 py-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 justify-center md:justify-between">
        <span className="inline-flex items-center gap-1.5">
          <Truck className="size-3.5" />
          Versand 24h — Lieferung in DE 1–3 Werktage
        </span>
        <span className="hidden md:inline-flex items-center gap-1.5">
          <PackageCheck className="size-3.5" />
          Sendungsverfolgung per Mail
        </span>
        {hasWhatsApp && (
          <span className="hidden md:inline-flex items-center gap-1.5">
            <Headphones className="size-3.5" />
            Direkter Kontakt zum Agent
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Brand-Identity links (Logo oder Name + Initial-Bubble)
// ============================================================

function BrandIdentity({
  page,
  logoUrl,
  fullName,
  initials,
  theme,
  clickable = true,
}: {
  page?: PublicPage;
  logoUrl: string | null;
  fullName: string;
  initials: string;
  theme: ThemeT;
  clickable?: boolean;
}) {
  const inner = logoUrl ? (
    <img
      src={logoUrl}
      alt={page?.headline || fullName}
      className="h-8 md:h-9 max-w-[180px] md:max-w-[240px] object-contain"
    />
  ) : (
    <div className="flex items-center gap-2">
      <div
        className="size-9 rounded-xl grid place-items-center text-white text-xs font-bold flex-shrink-0"
        style={{
          background: `linear-gradient(135deg, ${theme.gradientFrom}, ${theme.gradientTo})`,
        }}
      >
        {initials || "DP"}
      </div>
      <div className="min-w-0">
        <div
          className="text-sm font-bold leading-tight truncate"
          style={{ color: theme.ink }}
        >
          {page?.headline || fullName}
        </div>
        {page?.headline && (
          <div
            className="text-[10px] truncate leading-tight"
            style={{ color: theme.inkMuted }}
          >
            {fullName}
          </div>
        )}
      </div>
    </div>
  );

  if (!clickable) return inner;
  return (
    <button
      onClick={() => navigate("/")}
      className="flex items-center hover:opacity-80 transition-opacity"
      aria-label="Startseite"
    >
      {inner}
    </button>
  );
}

// ============================================================
// ShopHeader — Always-on Sticky-Header für alle Subdomain-Seiten
// ============================================================

export function ShopHeader({
  page,
  agentCode,
  theme,
  isDark,
  categoryList = [],
  activeCat = null,
  onSelectCat = () => {},
  showBackButton = false,
  centerSlot = null,
}: {
  page?: PublicPage;
  agentCode: string;
  theme: ThemeT;
  isDark: boolean;
  categoryList?: { name: string; count: number }[];
  activeCat?: string | null;
  onSelectCat?: (cat: string | null) => void;
  showBackButton?: boolean;
  centerSlot?: React.ReactNode;
}) {
  const fullName = page
    ? `${page.agent_first_name} ${page.agent_last_name}`.trim()
    : agentCode;
  const initials = page
    ? `${page.agent_first_name?.[0] || ""}${page.agent_last_name?.[0] || ""}`.toUpperCase()
    : "DP";
  const logoUrl = absoluteUploadUrl(page?.logo_url || null);
  const isB2bLocked = page?.is_b2b_locked ?? false;

  return (
    <div
      className="sticky top-0 z-40 border-b"
      style={{
        background: isDark ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.95)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      <div className="mx-auto max-w-6xl px-3 md:px-4 py-2.5 flex items-center gap-2 md:gap-3">
        {showBackButton && (
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1 text-xs md:text-sm hover:underline -ml-1"
            style={{ color: theme.ink }}
            aria-label="Zurück"
          >
            <ArrowLeft className="size-4" />
            <span className="hidden sm:inline">Zurück</span>
          </button>
        )}

        {/* Brand links — clickbar zur Home */}
        <div className={showBackButton ? "flex-shrink-0" : "min-w-0 flex-shrink-0"}>
          <BrandIdentity
            page={page}
            logoUrl={logoUrl}
            fullName={fullName}
            initials={initials}
            theme={theme}
          />
        </div>

        {/* Center-Slot — Such-Slot oder leer */}
        <div className="flex-1 min-w-0">{centerSlot}</div>

        {/* Action-Buttons rechts */}
        <div className="flex items-center gap-1 md:gap-1.5 flex-shrink-0">
          {categoryList.length > 0 && (
            <CategoryMenu
              categories={categoryList}
              activeCat={activeCat}
              onSelect={onSelectCat}
              theme={theme}
              isDark={isDark}
            />
          )}
          {!isB2bLocked && (
            <>
              <WishlistButton
                agentCode={agentCode}
                theme={theme}
                isDark={isDark}
              />
              <MiniCartButton
                agentCode={agentCode}
                theme={theme}
                isDark={isDark}
              />
            </>
          )}
          <CustomerProfileButton
            theme={theme}
            isDark={isDark}
            isB2bLocked={isB2bLocked}
            registerUrl={page?.register_url || null}
            compact
          />
        </div>
      </div>
    </div>
  );
}
